import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { useTranslation } from 'react-i18next';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const apiClient = useApiClient();
  const { addToast } = useToast();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      await apiClient.forgotPassword(email);
      setSent(true);
      addToast(t('auth.forgotPassword.successToast'), 'success');
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err, t('auth.forgotPassword.errorToast'));
      addToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center space-y-4">
        <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-neutral-900">{t('auth.forgotPassword.sentTitle')}</h2>
        <p className="text-sm text-neutral-600">
          {t('auth.forgotPassword.sentDescription', { email })}
        </p>
        <p className="text-xs text-neutral-500 mt-2">{t('auth.forgotPassword.sentExpiry')}</p>
        <Link to="/login" className="inline-block mt-4 text-primary-600 hover:text-primary-700 font-semibold text-sm">
          {t('auth.backToLogin')}
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center mb-4">
        <h2 className="text-xl font-bold text-neutral-900">{t('auth.forgotPassword.title')}</h2>
        <p className="text-sm text-neutral-600 mt-2">{t('auth.forgotPassword.description')}</p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-neutral-900 mb-2">
          {t('auth.email')}
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('auth.emailPlaceholder')}
          disabled={isLoading}
          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 border-neutral-300 focus:ring-primary-500"
          required
        />
      </div>

      <button
        type="submit"
        disabled={isLoading || !email}
        className="w-full px-4 py-2 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isLoading ? t('auth.forgotPassword.loading') : t('auth.forgotPassword.submit')}
      </button>

      <p className="text-center text-sm text-neutral-600">
        <Link to="/login" className="text-primary-600 hover:text-primary-700 font-semibold">
          {t('auth.backToLogin')}
        </Link>
      </p>
    </form>
  );
}
